/**
 * Canonicalises a registrant-contact phone number into the `+<calling code>.<subscriber>` spelling
 * registrars require, accepting what customers actually type.
 *
 * A faithful port of the backend's `Service\RegistrationPlatform\RegistrarPhone` (rules + fixed
 * rejection messages documented in `docs/api-types/domain-names.md`, *Upsert the registrant
 * contact*). The backend canonicalises on every write regardless — `PUT
 * /datacenters/domain-names/{id}/contact` and the `contact` object on `POST
 * /datacenters/domain-orders` — so this exists purely so the value the user sees is the value that
 * gets stored, instead of the field silently diverging after save.
 *
 * Why it matters: GoDaddy matches `phone` against E.164-with-a-dot and rejects the keypad shape
 * `+32473999803` with `contacts.contactRegistrant.phone=MISMATCH_FORMAT` — a terminal failure on an
 * irreversible money path (it killed the first live `.be` trade on 2026-08-31).
 *
 * Accepted inputs, all yielding `+32.473999803`:
 *
 *   +32.473999803        already canonical      +32473999803         keypad E.164
 *   +32 473/99.98.03     human formatting       0032 473 99 98 03    ITU `00` access code
 *   +32 (0)473 99 98 03  trunk zero in parens   0473 99 98 03        national — needs `isoCountry`
 *
 * NEVER block a submit on this helper: it returns a result object rather than throwing, and the
 * backend is the authority. A rejection is a hint, not a gate — if this port ever disagrees with the
 * server on an edge case, the server wins and its own `data.phone` message is what the user sees.
 */

/** Why a value could not be canonicalised. Each maps 1:1 to a fixed backend rejection message. */
export type RegistrarPhoneRejection =
  | 'required'
  | 'too_long'
  | 'bad_characters'
  | 'needs_country'
  | 'unknown_code'
  | 'bad_length';

export type RegistrarPhoneResult =
  | { ok: true; value: string }
  | { ok: false; reason: RegistrarPhoneRejection; message: string };

/**
 * The `phone` column's cap, checked by the backend on the RAW value (before canonicalisation) — so
 * an input field may cap at this but never below it. The canonical output is at most 19 characters.
 */
export const MAX_REGISTRAR_PHONE_LENGTH = 32;

/** Longest calling code in {@link CALLING_CODES} — where the longest-prefix match starts. */
const MAX_CODE_LENGTH = 3;

/** E.164 caps the whole number (calling code + subscriber) at 15 digits. */
const MAX_TOTAL_DIGITS = 15;

/** Shortest real-world total: a 3-digit code plus a 4-digit subscriber (e.g. `+290 XXXX`). */
const MIN_TOTAL_DIGITS = 7;

/** A subscriber number shorter than this is a typo, not a phone number. */
const MIN_SUBSCRIBER_DIGITS = 4;

/**
 * Calling codes whose leading `0` is PART of the number rather than a national trunk prefix: an
 * Italian landline really is `+39.06…`, not `+39.6…`. `39` covers IT and VA, `378` covers SM — the
 * standard exceptions to E.164's "drop the trunk code". Keyed on the RESOLVED calling code, so the
 * rule applies whether the number arrived international (`+39 06…`) or national (`06…` + `IT`).
 */
const KEEPS_LEADING_ZERO_CODES = ['39', '378'];

/**
 * ISO 3166-1 alpha-2 → ITU country calling code. Generated from the backend's own
 * `RegistrarPhone::CALLING_CODES` so the two canonicalise identically; shared codes are intentional
 * (NANP territories all map to `1`; `44`/`590`/`599`/`262`/`61`/`47` cover their dependencies).
 *
 * Deliberately NOT taken from `countries-list`'s `phone` field, even though the project sources all
 * other country data from that package: it carries NANP *area* codes (`AG` → `1268`) and its own
 * spellings for `VA` (`379`), `SJ` (`4779`), `BQ`/`CW` (`5997`/`5999`), none of which the backend
 * map knows — using them would make the client rewrite numbers the server then rewrites differently,
 * which is the exact divergence this helper exists to remove.
 */
const CALLING_CODES: Record<string, string> = {
  AD: '376',
  AE: '971',
  AF: '93',
  AG: '1',
  AI: '1',
  AL: '355',
  AM: '374',
  AO: '244',
  AQ: '672',
  AR: '54',
  AS: '1',
  AT: '43',
  AU: '61',
  AW: '297',
  AX: '358',
  AZ: '994',
  BA: '387',
  BB: '1',
  BD: '880',
  BE: '32',
  BF: '226',
  BG: '359',
  BH: '973',
  BI: '257',
  BJ: '229',
  BL: '590',
  BM: '1',
  BN: '673',
  BO: '591',
  BQ: '599',
  BR: '55',
  BS: '1',
  BT: '975',
  BW: '267',
  BY: '375',
  BZ: '501',
  CA: '1',
  CC: '61',
  CD: '243',
  CF: '236',
  CG: '242',
  CH: '41',
  CI: '225',
  CK: '682',
  CL: '56',
  CM: '237',
  CN: '86',
  CO: '57',
  CR: '506',
  CU: '53',
  CV: '238',
  CW: '599',
  CX: '61',
  CY: '357',
  CZ: '420',
  DE: '49',
  DJ: '253',
  DK: '45',
  DM: '1',
  DO: '1',
  DZ: '213',
  EC: '593',
  EE: '372',
  EG: '20',
  EH: '212',
  ER: '291',
  ES: '34',
  ET: '251',
  FI: '358',
  FJ: '679',
  FK: '500',
  FM: '691',
  FO: '298',
  FR: '33',
  GA: '241',
  GB: '44',
  GD: '1',
  GE: '995',
  GF: '594',
  GG: '44',
  GH: '233',
  GI: '350',
  GL: '299',
  GM: '220',
  GN: '224',
  GP: '590',
  GQ: '240',
  GR: '30',
  GT: '502',
  GU: '1',
  GW: '245',
  GY: '592',
  HK: '852',
  HN: '504',
  HR: '385',
  HT: '509',
  HU: '36',
  ID: '62',
  IE: '353',
  IL: '972',
  IM: '44',
  IN: '91',
  IO: '246',
  IQ: '964',
  IR: '98',
  IS: '354',
  IT: '39',
  JE: '44',
  JM: '1',
  JO: '962',
  JP: '81',
  KE: '254',
  KG: '996',
  KH: '855',
  KI: '686',
  KM: '269',
  KN: '1',
  KP: '850',
  KR: '82',
  KW: '965',
  KY: '1',
  KZ: '7',
  LA: '856',
  LB: '961',
  LC: '1',
  LI: '423',
  LK: '94',
  LR: '231',
  LS: '266',
  LT: '370',
  LU: '352',
  LV: '371',
  LY: '218',
  MA: '212',
  MC: '377',
  MD: '373',
  ME: '382',
  MF: '590',
  MG: '261',
  MH: '692',
  MK: '389',
  ML: '223',
  MM: '95',
  MN: '976',
  MO: '853',
  MP: '1',
  MQ: '596',
  MR: '222',
  MS: '1',
  MT: '356',
  MU: '230',
  MV: '960',
  MW: '265',
  MX: '52',
  MY: '60',
  MZ: '258',
  NA: '264',
  NC: '687',
  NE: '227',
  NF: '672',
  NG: '234',
  NI: '505',
  NL: '31',
  NO: '47',
  NP: '977',
  NR: '674',
  NU: '683',
  NZ: '64',
  OM: '968',
  PA: '507',
  PE: '51',
  PF: '689',
  PG: '675',
  PH: '63',
  PK: '92',
  PL: '48',
  PM: '508',
  PR: '1',
  PS: '970',
  PT: '351',
  PW: '680',
  PY: '595',
  QA: '974',
  RE: '262',
  RO: '40',
  RS: '381',
  RU: '7',
  RW: '250',
  SA: '966',
  SB: '677',
  SC: '248',
  SD: '249',
  SE: '46',
  SG: '65',
  SH: '290',
  SI: '386',
  SJ: '47',
  SK: '421',
  SL: '232',
  SM: '378',
  SN: '221',
  SO: '252',
  SR: '597',
  SS: '211',
  ST: '239',
  SV: '503',
  SX: '1',
  SY: '963',
  SZ: '268',
  TC: '1',
  TD: '235',
  TF: '262',
  TG: '228',
  TH: '66',
  TJ: '992',
  TK: '690',
  TL: '670',
  TM: '993',
  TN: '216',
  TO: '676',
  TR: '90',
  TT: '1',
  TV: '688',
  TW: '886',
  TZ: '255',
  UA: '380',
  UG: '256',
  US: '1',
  UY: '598',
  UZ: '998',
  VA: '39',
  VC: '1',
  VE: '58',
  VG: '1',
  VI: '1',
  VN: '84',
  VU: '678',
  WF: '681',
  WS: '685',
  YE: '967',
  YT: '262',
  ZA: '27',
  ZM: '260',
  ZW: '263',
};

/** The set of assigned calling codes — the reverse index an international number is matched against. */
const KNOWN_CODES = new Set(Object.values(CALLING_CODES));

/**
 * The number's country calling code by LONGEST-PREFIX match, or `null` when nothing matches.
 *
 * Longest-first is load-bearing: `353…` must read as Ireland (353), not the unassigned `35` or `3`.
 * The contact's own country wins when the number already starts with its code, which settles the
 * shared codes (`+1` NANP, `+7` RU/KZ, `+44` GB/JE/GG/IM) in favour of what the contact declared. A
 * match must leave at least one subscriber digit behind.
 */
const splitCallingCode = (digits: string, preferred: string | null): string | null => {
  if (preferred !== null && digits.startsWith(preferred) && digits.length > preferred.length) {
    return preferred;
  }

  for (let length = MAX_CODE_LENGTH; length >= 1; length--) {
    if (digits.length <= length) {
      continue; // nothing would be left for the subscriber
    }
    const candidate = digits.slice(0, length);
    if (KNOWN_CODES.has(candidate)) {
      return candidate;
    }
  }
  return null;
};

/**
 * Canonicalise `raw` to `+<calling code>.<subscriber>`.
 *
 * @param raw        what the customer typed.
 * @param isoCountry the contact's ISO 3166-1 alpha-2 country — REQUIRED to parse a number with no
 *                   international prefix, and otherwise only the preferred calling code when the
 *                   number already starts with it.
 */
export const canonicalizeRegistrarPhone = (raw: string, isoCountry?: string | null): RegistrarPhoneResult => {
  const value = raw.trim();
  if (value === '') {
    return { ok: false, reason: 'required', message: 'A phone number is required' };
  }
  // The column cap is checked on the raw value, before canonicalisation — mirroring the backend's
  // `DomainContactRequest`, so a too-long input is reported here rather than shortened into shape.
  if (value.length > MAX_REGISTRAR_PHONE_LENGTH) {
    return {
      ok: false,
      reason: 'too_long',
      message: `phone must be at most ${MAX_REGISTRAR_PHONE_LENGTH} characters`,
    };
  }

  // Fold the separators humans and locales inject. Anything left that is not a digit or a single
  // leading '+' is a hard reject — we never silently drop meaningful characters.
  let compact = value.replace(/[\s.\-()/]+/g, '');

  // '00' is the ITU international access code — the dialled spelling of '+'. Fold it first so
  // '0032…' reads as international rather than as a national number with a trunk zero.
  if (compact.startsWith('00')) {
    compact = `+${compact.slice(2)}`;
  }

  const international = compact.startsWith('+');
  const digits = international ? compact.slice(1) : compact;
  if (!/^[0-9]+$/.test(digits)) {
    return {
      ok: false,
      reason: 'bad_characters',
      message:
        'A phone number must be digits with an optional leading + or 00' +
        ' (spaces, dots, dashes, slashes and parentheses are allowed)',
    };
  }

  const iso = typeof isoCountry === 'string' ? isoCountry.trim().toUpperCase() : '';
  const declared = CALLING_CODES[iso] ?? null;

  let code: string;
  let subscriber: string;

  if (international) {
    const matched = splitCallingCode(digits, declared);
    if (matched === null) {
      return {
        ok: false,
        reason: 'unknown_code',
        message: 'The international dialling code in the phone number is not recognised',
      };
    }
    code = matched;
    subscriber = digits.slice(matched.length);
  } else {
    if (declared === null) {
      return {
        ok: false,
        reason: 'needs_country',
        message:
          'A phone number without an international prefix needs a country:' +
          ' write it as +<country code> <number> (e.g. +32 473 99 98 03)',
      };
    }
    code = declared;
    subscriber = digits;
  }

  // A single leading zero on the subscriber is the national TRUNK prefix, which the international
  // form drops. Applied to both paths on purpose: it is how the very common `+32 (0)473 …` /
  // `+33 (0)6 …` spelling is written, and the parentheses are gone by now.
  if (subscriber.startsWith('0') && !KEEPS_LEADING_ZERO_CODES.includes(code)) {
    subscriber = subscriber.slice(1);
  }

  const total = code.length + subscriber.length;
  if (subscriber.length < MIN_SUBSCRIBER_DIGITS || total < MIN_TOTAL_DIGITS || total > MAX_TOTAL_DIGITS) {
    return {
      ok: false,
      reason: 'bad_length',
      message: `A phone number must hold between ${MIN_TOTAL_DIGITS} and ${MAX_TOTAL_DIGITS} digits including the country code`,
    };
  }

  return { ok: true, value: `+${code}.${subscriber}` };
};

/** Whether a value is already canonical — the cheap guard for "must I rewrite this?". */
export const isCanonicalRegistrarPhone = (value: string): boolean => /^\+[0-9]{1,3}\.[0-9]{4,14}$/.test(value);

/**
 * Best-effort canonicalisation for a request payload: the canonical form when parseable, else the
 * trimmed raw value so the backend can canonicalise it (or reject it with its own message). Never
 * throws and never drops the user's input — a payload builder must not be a validation gate.
 */
export const canonicalizeRegistrarPhoneOrRaw = (raw: string, isoCountry?: string | null): string => {
  const result = canonicalizeRegistrarPhone(raw, isoCountry);
  return result.ok ? result.value : raw.trim();
};
